import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../../shared/lib/supabase'

const HIGH_RISK_GATES = new Set(['high'])

// Pure helper. Every high-risk finding must have an explicit disposition
// (anything other than 'pending') before Verify is enabled.
export function allHighRiskDispositioned(riskMap, dispositionMap) {
  for (const [id, risk] of Object.entries(riskMap || {})) {
    if (!HIGH_RISK_GATES.has(risk?.level)) continue
    const disposition = dispositionMap?.[id]?.disposition || 'pending'
    if (disposition === 'pending') return false
  }
  return true
}

// Pure helper. Overlay advisor edits on the AI's finding output for rendering.
export function mergeEdits(finding, edits) {
  if (!edits || Object.keys(edits).length === 0) return finding
  return { ...finding, ...edits }
}

// The hook. Owns per-finding disposition state and persists to Supabase.
// findings: Array of finding rows from useInsuranceReview.
// onChanged: optional callback fired after any successful write.
export default function useFindingDisposition({ findings, onChanged } = {}) {
  const [dispositionMap, setDispositionMap] = useState({})
  const [userId, setUserId] = useState(null)

  useEffect(() => {
    let cancelled = false
    supabase.auth.getUser().then(({ data }) => {
      if (!cancelled) setUserId(data?.user?.id ?? null)
    })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!Array.isArray(findings)) return
    const initial = {}
    for (const f of findings) {
      initial[f.id] = {
        disposition: f.advisor_disposition || 'pending',
        edits: pickEdits(f)
      }
    }
    setDispositionMap(initial)
  }, [findings])

  const setDisposition = useCallback(async (findingId, disposition) => {
    if (!userId) return
    setDispositionMap(prev => ({
      ...prev,
      [findingId]: { ...(prev[findingId] || {}), disposition }
    }))
    const { error } = await supabase
      .from('client_insurance_review_findings')
      .update({
        advisor_disposition: disposition,
        advisor_disposition_at: new Date().toISOString(),
        advisor_disposition_by: userId
      })
      .eq('id', findingId)
    if (error) {
      console.error('[disposition] persist failed:', error)
    } else {
      onChanged?.()
    }
  }, [onChanged, userId])

  const setEdits = useCallback(async (findingId, edits, originalFinding) => {
    if (!userId) return
    setDispositionMap(prev => ({
      ...prev,
      [findingId]: {
        ...(prev[findingId] || {}),
        disposition: 'modified',
        edits
      }
    }))
    // original_ai_output is the E&O snapshot of the AI's unedited output. It is
    // populated once by the server-side pipeline on INSERT (run-pipeline.js) and
    // must never be written from the browser — the client only ever appends its
    // own edits (advisor_edits / the individual field patch below).
    const patch = {
      advisor_disposition: 'modified',
      advisor_disposition_at: new Date().toISOString(),
      advisor_disposition_by: userId,
      advisor_edited_at: new Date().toISOString(),
      ...edits
    }
    const { error } = await supabase
      .from('client_insurance_review_findings')
      .update(patch)
      .eq('id', findingId)
    if (error) {
      console.error('[disposition] edit persist failed:', error)
    } else {
      onChanged?.()
    }
  }, [onChanged, userId])

  const gate = useCallback((riskMap) => allHighRiskDispositioned(riskMap, dispositionMap), [dispositionMap])

  return { dispositionMap, setDisposition, setEdits, allDispositioned: gate }
}

function pickEdits(f) {
  // Only surface edits distinct from the original AI output. If the finding
  // has never been edited, edits is null.
  if (!f.advisor_edited_at || !f.original_ai_output) return null
  const edits = {}
  for (const key of ['title', 'detail', 'business_impact', 'rationale']) {
    if (f[key] !== f.original_ai_output[key]) edits[key] = f[key]
  }
  return Object.keys(edits).length > 0 ? edits : null
}
